import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchItems } from '../queries';
import { RootState } from '../store';
import { SalesforceFieldConfig, SalesforcePermissionGroup, SalesforcePermissions } from '../../types/salesforce';

interface SalesforcePermissionState {
  salesforcePermissions: SalesforcePermissions;
  loading: boolean,
  error: string | null,
}

const initialState: SalesforcePermissionState = {
  salesforcePermissions: {},
  loading: false,
  error: null,
};

interface PermissionsCache {
  [id: string]: {
    lastPermissions: SalesforcePermissionGroup,
    lastResult: SalesforceFieldConfig[]
  }
}

const permissionsCache: PermissionsCache = {};

export const fetchSalesforcePermissions = createAsyncThunk<SalesforcePermissions, void, { state: RootState }>(
  'salesforcePermissions/fetchSalesforcePermissions',
  async (_: void, { getState }) => {
    const state = getState() as RootState;
    const fetchedPermissions = await fetchItems('sf/permissions', { email: state.user.userData.email });
    return fetchedPermissions;
  }
);

export const checkSalesforcePermissionsLoaded = () => async (dispatch, getState) => {
  const state = getState() as RootState;
  if (Object.keys(state.salesforcePermissions.salesforcePermissions).length === 0 && !state.salesforcePermissions.loading) {
    await dispatch(fetchSalesforcePermissions());
  }
};

export const selectSalesforcePermissionById = (state: RootState, id: string) => {
  const permissions: SalesforcePermissionGroup = state.salesforcePermissions.salesforcePermissions[id]?.permissions;
  if (!permissionsCache[id]) {
    permissionsCache[id] = { lastPermissions: undefined, lastResult: [] };
  }
  if (permissions === permissionsCache[id].lastPermissions) {
    return permissionsCache[id].lastResult;
  }
  if (!permissions) {
    permissionsCache[id].lastPermissions = permissions;
    permissionsCache[id].lastResult = [];
    return [];
  }
  const result = Object.values(permissions).flatMap(property => Object.values(property) as SalesforceFieldConfig[]) || [];
  permissionsCache[id].lastPermissions = permissions;
  permissionsCache[id].lastResult = result;
  return result;
};

const salesforcePermissionsSlice = createSlice({
  name: 'salesforcePermissions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesforcePermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesforcePermissions.fulfilled, (state, action: PayloadAction<SalesforcePermissions>) => {
        state.salesforcePermissions = action.payload;
        state.loading = false;
      })
      .addCase(fetchSalesforcePermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default salesforcePermissionsSlice.reducer;